import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useAuthHandler } from 'views/auth/useAuth';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useToast } from 'views/mutual/toast/hooks';
import { useCorporateAccountEstablishStore } from 'views/templates/accountOpen/hooks';
import { usePersonalAccountEstablishStore } from 'views/templates/personalAccountOpen/hooks';
import { useLoginMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { NavLink, useNavigate } from 'react-router-dom';
import { PagePath } from 'constants/pagePath';

type LoginParamType = {
  account: '';
  password: '';
};

export const useLogin = () => {
  const [loginData, login] = useLoginMutation();
  const [isRevealPassword, setRevealPassword] = useState<boolean>(false);
  const { toastProps, showToast } = useToast();
  const { clear: clearCorporate } = useCorporateAccountEstablishStore();
  const { clear: clearPersonal } = usePersonalAccountEstablishStore();
  const { onLoggedInSuccess } = useAuthHandler();
  const { showError } = useGlobalToastHandler();
  const navigte = useNavigate();

  useEffect(() => {
    clearCorporate();
    clearPersonal();
  }, [clearCorporate, clearPersonal]);

  const validate = (values: LoginParamType) => {
    const errors: Partial<{
      account: string;
      password: string;
    }> = {};

    if (!values.account) {
      errors.account = '口座番号が未入力です。';
    }
    if (!values.password) {
      errors.password = 'パスワードが未入力です。';
    }
    return errors;
  };

  const togglePassword = () => {
    setRevealPassword(!isRevealPassword);
  };

  const onSubmit = useOnceSubmit<LoginParamType>(
    {
      onSubmit: async (values) => {
        const ret = await login({
          input: {
            Account: values.account,
            Password: values.password,
          },
        });
        // アクティベーションコード入力画面遷移の分岐(Codeが99)
        if (ret.data?.Login?.Code === 99) {
          navigte(PagePath.ActivationCode, {
            state: {
              provisionalToken: ret.data.Login.ProvisionalToken,
            },
          });
        } else if (ret.data && ret.data?.Login?.Code !== 0) {
          showError(ret.data?.Login?.Message ?? 'ログインに失敗しました');
        } else if (ret.data?.Login?.Code === 0) {
          onLoggedInSuccess({
            text: ret.data.Login.DisplayText || '',
            provisionalToken: ret.data.Login.ProvisionalToken || '',
          });
        }
      },
    },
    [showToast, onLoggedInSuccess],
  );

  const formik = useFormik<LoginParamType>({
    initialValues: {
      account: '',
      password: '',
    },
    onSubmit,
    validate,
    validateOnChange: false,
    validateOnBlur: false,
  });

  return { isRevealPassword, togglePassword, formik, toastProps };
};
